import { auditSiteDiscoverability } from '@api/company';
import { Analytics } from '@analytics';
import { Button, CircularProgress, TextField } from '@material-ui/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import CustomSection from '../CustomSection';

// Card Y5QiKDCL — AI-Discoverability Benchmark. The "AI Readiness" module at the
// top of Setup: scan the operator's whole site and show a plain checklist of how
// legible it is to AI assistants (ChatGPT, Gemini, Perplexity), with a concrete
// fix per failing item. Read-only — this changes nothing on the operator's site,
// it just tells them what to fix (and quietly seeds a support/sales conversation).

// The Tours section dispatches this to "run it automatically and scroll back up".
export const RUN_AI_READINESS_EVENT = 'td:run-ai-readiness';

const storageKey = companyId => `td-ai-readiness-url-${companyId}`;

const weightPill = {
  critical: 'bg-red-100 text-red-700',
  important: 'bg-amber-100 text-amber-700',
  nice: 'bg-slate-100 text-slate-500'
};

const CheckRow = ({ check }) => (
  <div className="flex gap-3 py-3 border-b border-slate-100 last:border-0">
    <div
      className={`mt-0.5 flex-none w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white ${
        check.passed ? 'bg-emerald-500' : 'bg-red-500'
      }`}
      aria-hidden>
      {check.passed ? '✓' : '!'}
    </div>
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold text-slate-800">{check.label}</span>
        {!check.passed && (
          <span
            className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
              weightPill[check.weight] || weightPill.nice
            }`}>
            {check.weight}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-0">{check.detail}</p>
      {!check.passed && (
        <p className="text-xs text-slate-700 mt-1 mb-0">
          <span className="font-semibold">How to fix: </span>
          {check.fix}
        </p>
      )}
    </div>
  </div>
);

const AiReadinessSection = ({ companyId }) => {
  const [url, setUrl] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const rootRef = useRef(null);
  const urlRef = useRef('');
  urlRef.current = url;

  // Remember the last-scanned URL per company so re-visits (and the Tours
  // jump-link) can run without retyping it.
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(storageKey(companyId));
      if (saved) setUrl(saved);
    } catch (e) {
      /* localStorage unavailable — ignore */
    }
  }, [companyId]);

  const runAudit = useCallback(
    async targetUrl => {
      const siteUrl = (targetUrl || urlRef.current || '').trim();
      if (!siteUrl || isScanning) return;
      setIsScanning(true);
      setError('');
      try {
        window.localStorage.setItem(storageKey(companyId), siteUrl);
      } catch (e) {
        /* ignore */
      }
      try {
        const data = await auditSiteDiscoverability(companyId, siteUrl);
        setResult(data);
        Analytics.track('AI readiness scan run', {
          criticalFailures: data?.criticalFailures
        });
      } catch (e) {
        // Card QyIKo5Eb — blocked sources (TripAdvisor/Viator) and malformed
        // addresses come back as DomainErrors whose message already tells the
        // operator what to fix, so prefer it over the catch-all.
        setError(
          e?.response?.data?.error?.message ||
            "We couldn't scan that address. Check it's your public website URL and try again."
        );
      } finally {
        setIsScanning(false);
      }
    },
    [companyId, isScanning]
  );

  // A jump-link elsewhere can fire this (the Tours one is gone): scroll the module into view, then auto-run
  // if we already know the URL, otherwise focus the field so they can enter it.
  useEffect(() => {
    const onRun = () => {
      if (rootRef.current) {
        rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (urlRef.current.trim()) {
        runAudit(urlRef.current);
      }
    };
    window.addEventListener(RUN_AI_READINESS_EVENT, onRun);
    return () => window.removeEventListener(RUN_AI_READINESS_EVENT, onRun);
  }, [runAudit]);

  const criticalFailures = result?.criticalFailures ?? 0;
  const allGood = result && criticalFailures === 0;

  return (
    <div ref={rootRef}>
      <CustomSection
        id="AiReadiness"
        isForm={false}
        title="AI Readiness"
        description="See how visible your tours are to AI assistants like ChatGPT, Gemini and Perplexity — and exactly what to fix so they can recommend you.">
        <div className="flex flex-col sm:flex-row gap-2 items-stretch">
          <TextField
            size="small"
            className="flex-1"
            placeholder="https://yourwebsite.com"
            variant="outlined"
            value={url}
            disabled={isScanning}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runAudit()}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            onClick={() => runAudit()}
            disabled={isScanning || !url.trim()}
            variant="contained"
            color="primary"
            className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !rounded-xl !shadow-sm !normal-case whitespace-nowrap">
            {isScanning ? (
              <span className="flex items-center gap-2">
                <CircularProgress size={16} color="inherit" />
                Scanning…
              </span>
            ) : result ? (
              'Re-scan'
            ) : (
              'Scan my site'
            )}
          </Button>
        </div>

        {error && <p className="text-xs text-red-600 mt-2 mb-0">{error}</p>}

        {result && (
          <div className="mt-4">
            {/* Card 265 — the address answered 200 with a "page not found"
                screen, so the score below is for an error page. */}
            {result.warning && (
              <p
                role="alert"
                className="rounded-xl px-4 py-3 mb-2 mt-0 text-sm bg-amber-50 text-amber-900">
                {result.warning.message}
              </p>
            )}
            <div
              className={`rounded-xl px-4 py-3 text-sm font-semibold ${
                allGood
                  ? 'bg-emerald-50 text-emerald-800'
                  : 'bg-amber-50 text-amber-900'
              }`}>
              {allGood
                ? `Looking good — your site passed all ${result.passedCount} of ${result.totalCount} AI-readiness checks.`
                : `${criticalFailures} issue${
                    criticalFailures === 1 ? '' : 's'
                  } may stop AI assistants from reading and recommending your tours. ${
                    result.passedCount
                  }/${result.totalCount} checks passed.`}
            </div>
            <div className="mt-2">
              {result.checks.map(check => (
                <CheckRow key={check.key} check={check} />
              ))}
            </div>
            {!allGood && (
              <p className="text-xs text-slate-500 mt-3 mb-0">
                Not sure how to fix these? Reply to your TipDirect welcome email and
                we&apos;ll help you get AI-ready.
              </p>
            )}
          </div>
        )}
      </CustomSection>
    </div>
  );
};

export default AiReadinessSection;
